import React from 'react';
import { Grid, Card, CardContent, Box, Chip, Typography } from '@material-ui/core';
import { CombinedReleaseHistory } from '@internal/backstage-plugin-ewii-azure';
import { CustomDateRange } from '../hooks/useFilters';

interface SummaryCardsProps {
  releaseSummary: any;
  healthSummary: any;
  customDateRange: CustomDateRange | null;
  releaseHistory: Record<string, CombinedReleaseHistory[]>;
  onCardClick: (type: 'recent' | 'health', timePeriod?: '24h' | '7d' | '30d', environment?: string) => void;
  isCardActive: (type: 'recent' | 'health', timePeriod?: '24h' | '7d' | '30d', environment?: string) => boolean;
}

export const SummaryCards: React.FC<SummaryCardsProps> = React.memo(({ 
  releaseSummary, 
  healthSummary, 
  customDateRange, 
  releaseHistory, 
  onCardClick, 
  isCardActive 
}) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card style={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" component="h2">
            Last 24 Hours
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total releases deployed
          </Typography>
          <Box display="flex" marginTop={1} style={{ gap: '8px', flexWrap: 'wrap' }}>
            <Chip 
              label={`${releaseSummary.last24Hours.test} test`} 
              color={isCardActive('recent', '24h', 'test') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '24h', 'test')}
            />
            <Chip 
              label={`${releaseSummary.last24Hours.acceptance} acceptance`} 
              color={isCardActive('recent', '24h', 'acceptance') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '24h', 'acceptance')}
            />
            <Chip 
              label={`${releaseSummary.last24Hours.production} production`} 
              color={isCardActive('recent', '24h', 'production') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '24h', 'production')}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card style={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" component="h2">
            Last 7 Days
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total releases deployed
          </Typography>
          <Box display="flex" marginTop={1} style={{ gap: '8px', flexWrap: 'wrap' }}>
            <Chip 
              label={`${releaseSummary.last7Days.test} test`} 
              color={isCardActive('recent', '7d', 'test') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '7d', 'test')}
            />
            <Chip 
              label={`${releaseSummary.last7Days.acceptance} acceptance`} 
              color={isCardActive('recent', '7d', 'acceptance') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '7d', 'acceptance')}
            />
            <Chip 
              label={`${releaseSummary.last7Days.production} production`} 
              color={isCardActive('recent', '7d', 'production') ? "default" : "primary"}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('recent', '7d', 'production')}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card style={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" component="h2">
            {customDateRange
              ? `${new Date(customDateRange.from).toLocaleDateString('da-DK')} - ${new Date(customDateRange.to).toLocaleDateString('da-DK')}`
              : 'Last 30 Days'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total releases deployed
          </Typography>
          <Box display="flex" marginTop={1} style={{ gap: '8px', flexWrap: 'wrap' }}>
            {(() => {
              const summary = customDateRange
                ? {
                    test: (releaseHistory['test_custom'] || []).length,
                    acceptance: (releaseHistory['acceptance_custom'] || []).length,
                    production: (releaseHistory['production_custom'] || []).length,
                  }
                : releaseSummary.last30Days;
              return (
                <>
                  <Chip
                    label={`${summary.test} test`}
                    color={isCardActive('recent', customDateRange ? undefined : '30d', 'test') ? "default" : "primary"}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick('recent', customDateRange ? undefined : '30d', 'test')}
                  />
                  <Chip
                    label={`${summary.acceptance} acceptance`}
                    color={isCardActive('recent', customDateRange ? undefined : '30d', 'acceptance') ? "default" : "primary"}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick('recent', customDateRange ? undefined : '30d', 'acceptance')}
                  />
                  <Chip
                    label={`${summary.production} production`}
                    color={isCardActive('recent', customDateRange ? undefined : '30d', 'production') ? "default" : "primary"}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick('recent', customDateRange ? undefined : '30d', 'production')}
                  />
                </>
              );
            })()}
          </Box>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card style={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" component="h2">
            Health Status
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Services by health status
          </Typography>
          <Box display="flex" marginTop={1} style={{ gap: '8px', flexWrap: 'wrap' }}>
            <Chip 
              label={`${healthSummary.test.healthy} healthy / ${healthSummary.test.unhealthy} unhealthy`} 
              color={isCardActive('health', undefined, 'test') ? "default" : (healthSummary.test.unhealthy > 0 ? "secondary" : "primary")}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('health', undefined, 'test')}
            />
            <Chip 
              label={`${healthSummary.acceptance.healthy} healthy / ${healthSummary.acceptance.unhealthy} unhealthy`} 
              color={isCardActive('health', undefined, 'acceptance') ? "default" : (healthSummary.acceptance.unhealthy > 0 ? "secondary" : "primary")}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('health', undefined, 'acceptance')}
            />
            <Chip 
              label={`${healthSummary.production.healthy} healthy / ${healthSummary.production.unhealthy} unhealthy`} 
              color={isCardActive('health', undefined, 'production') ? "default" : (healthSummary.production.unhealthy > 0 ? "secondary" : "primary")}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick('health', undefined, 'production')}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)); 